import { useState } from 'react';

export default function HomePage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<string | null>(null);
  const [uploadedPhotos, setUploadedPhotos] = useState<Record<number, string>>({});
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedPhotoSlot, setSelectedPhotoSlot] = useState<number | null>(null);
  const [uploadedBentleyPhotos, setUploadedBentleyPhotos] = useState<Record<number, string>>({});
  const [showBentleyUploadModal, setShowBentleyUploadModal] = useState(false);
  const [selectedBentleyPhotoSlot, setSelectedBentleyPhotoSlot] = useState<number | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
    setFormData({ name: '', email: '', message: '' });
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && selectedPhotoSlot) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedPhotos(prev => ({
          ...prev,
          [selectedPhotoSlot]: reader.result as string
        }));
        setShowUploadModal(false);
        setSelectedPhotoSlot(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const openUploadModal = (photoNum: number) => {
    setSelectedPhotoSlot(photoNum);
    setShowUploadModal(true);
  };

  const handleBentleyPhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && selectedBentleyPhotoSlot) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedBentleyPhotos(prev => ({
          ...prev,
          [selectedBentleyPhotoSlot]: reader.result as string
        }));
        setShowBentleyUploadModal(false);
        setSelectedBentleyPhotoSlot(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const openBentleyUploadModal = (photoNum: number) => {
    setSelectedBentleyPhotoSlot(photoNum);
    setShowBentleyUploadModal(true);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                <i className="ri-upload-cloud-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-serif font-bold text-slate-900 mb-2">Upload Photo {selectedPhotoSlot}</h3>
              <p className="text-slate-600">Choose a photo of Nala to add to the gallery</p>
            </div>

            <div className="space-y-4">
              <label className="block">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  id="photo-upload"
                />
                <div className="border-2 border-dashed border-teal-300 rounded-2xl p-8 text-center cursor-pointer hover:border-teal-500 hover:bg-teal-50/50 transition-all">
                  <i className="ri-image-add-line text-5xl text-teal-600 mb-3 block"></i>
                  <p className="text-slate-700 font-medium mb-1">Click to select photo</p>
                  <p className="text-sm text-slate-500">JPG, PNG or GIF (max 10MB)</p>
                </div>
              </label>

              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedPhotoSlot(null);
                }}
                className="w-full px-6 py-3 bg-slate-200 text-slate-700 rounded-full font-medium hover:bg-slate-300 transition-all cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bentley Upload Modal */}
      {showBentleyUploadModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 flex items-center justify-center bg-orange-100 rounded-full mx-auto mb-4">
                <i className="ri-upload-cloud-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-2xl font-serif font-bold text-slate-900 mb-2">Upload Photo {selectedBentleyPhotoSlot}</h3>
              <p className="text-slate-600">Choose a photo of Bentley to add to the gallery</p>
            </div>

            <div className="space-y-4">
              <label className="block">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleBentleyPhotoUpload}
                  className="hidden"
                  id="bentley-photo-upload"
                />
                <div className="border-2 border-dashed border-orange-300 rounded-2xl p-8 text-center cursor-pointer hover:border-orange-500 hover:bg-orange-50/50 transition-all">
                  <i className="ri-image-add-line text-5xl text-orange-600 mb-3 block"></i>
                  <p className="text-slate-700 font-medium mb-1">Click to select photo</p>
                  <p className="text-sm text-slate-500">JPG, PNG or GIF (max 10MB)</p>
                </div>
              </label>

              <button
                onClick={() => {
                  setShowBentleyUploadModal(false);
                  setSelectedBentleyPhotoSlot(null);
                }}
                className="w-full px-6 py-3 bg-slate-200 text-slate-700 rounded-full font-medium hover:bg-slate-300 transition-all cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://static.readdy.ai/image/00835a872c246035d3f2fdf431da5d67/4539d659f03c970f77d9da74a1ca923d.jpeg" 
                alt="Family Logo" 
                className="w-12 h-12 rounded-full object-cover"
              />
              <span className="text-2xl font-serif font-bold text-slate-800">Family Memories</span>
            </div>
            <div className="flex items-center gap-8">
              <button onClick={() => scrollToSection('home')} className="text-sm font-medium text-slate-700 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap">Home</button>
              <button onClick={() => scrollToSection('gallery')} className="text-sm font-medium text-slate-700 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap">Gallery</button>
              <button onClick={() => scrollToSection('about')} className="text-sm font-medium text-slate-700 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap">About</button>
              <button onClick={() => scrollToSection('family-tree')} className="text-sm font-medium text-slate-700 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap">Family Tree</button>
              <button onClick={() => scrollToSection('pets')} className="text-sm font-medium text-slate-700 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap">Our Beloved Pets</button>
              <button onClick={() => scrollToSection('stories')} className="text-sm font-medium text-slate-700 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap">Family Stories</button>
              <button onClick={() => scrollToSection('contact')} className="text-sm font-medium text-slate-700 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap">Contact</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative pt-32 pb-20 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50">
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-transparent"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block px-4 py-2 bg-amber-100 rounded-full">
                <span className="text-sm font-medium text-amber-700">Est. 2025</span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-serif font-bold text-slate-900 leading-tight">
                The Start of a Thelwall Family Gallery(To be updated with more)
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed">
                Preserving precious moments and creating lasting memories for generations to come. Every photo tells a story of love, laughter, and togetherness.
              </p>
              <button onClick={() => scrollToSection('gallery')} className="inline-flex items-center gap-2 px-8 py-4 bg-amber-600 text-white rounded-full font-medium hover:bg-amber-700 transition-all hover:gap-3 cursor-pointer whitespace-nowrap">
                Explore Our Gallery
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-amber-200/30 rounded-full blur-2xl"></div>
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-rose-200/30 rounded-full blur-2xl"></div>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://static.readdy.ai/image/00835a872c246035d3f2fdf431da5d67/df072438963c894dc15b0403532b5632.jpeg" 
                  alt="Family Portrait" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-amber-100 rounded-full mb-4">
              <span className="text-xs font-semibold text-amber-700 tracking-wider uppercase">Our Memories</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-serif font-bold text-slate-900 mb-4">Family Photo Gallery</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A collection of our most cherished moments, captured through the years
            </p>
          </div>

          <div className="flex justify-center">
            <div className="group relative rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer w-full max-w-2xl h-96">
              <img 
                src="https://public.readdy.ai/ai/img_res/7b9c223f4e27e87166a295d571b83a37.jpg" 
                alt="Beach Vacation" 
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-6 left-6 text-white">
                  <p className="text-lg font-semibold">Holding Page - Photos to be Uploaded</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="absolute -top-8 -left-8 w-32 h-32 bg-emerald-200/30 rounded-full blur-2xl"></div>
              <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-amber-200/30 rounded-full blur-2xl"></div>
              <div className="relative w-96 h-96 mx-auto">
                <div className="w-full h-full rounded-full border-8 border-white shadow-2xl overflow-hidden">
                  <img 
                    src="https://static.readdy.ai/image/00835a872c246035d3f2fdf431da5d67/4539d659f03c970f77d9da74a1ca923d.jpeg" 
                    alt="Family Portrait" 
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="inline-block px-4 py-2 bg-emerald-100 rounded-full">
                <span className="text-xs font-semibold text-emerald-700 tracking-wider uppercase">Who We Are</span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-serif font-bold text-slate-900">The Thelwall Family</h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Welcome to our family photo gallery! We're the Andersons - a family of four who believes in capturing every precious moment and creating memories that will last a lifetime. Our journey together has been filled with love, laughter, and countless adventures.
                </p>
              </div>

              <div className="flex items-center gap-6 pt-4">
                <div className="text-center cursor-pointer group" onClick={() => scrollToSection('family-tree')}>
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-xl font-bold shadow-lg mb-2 group-hover:scale-110 transition-transform duration-300">
                    E
                  </div>
                  <p className="text-sm font-medium text-slate-700 group-hover:text-amber-600 transition-colors">Eddie</p>
                </div>
                <div className="text-center cursor-pointer group" onClick={() => scrollToSection('family-tree')}>
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center text-white text-xl font-bold shadow-lg mb-2 group-hover:scale-110 transition-transform duration-300">
                    L
                  </div>
                  <p className="text-sm font-medium text-slate-700 group-hover:text-amber-600 transition-colors">Laine</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Family Tree Section - NEW HORIZONTAL LAYOUT */}
      <section id="family-tree" className="py-24 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 overflow-x-auto">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-emerald-100 rounded-full mb-4">
              <span className="text-xs font-semibold text-emerald-700 tracking-wider uppercase">Our Heritage</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-serif font-bold text-slate-900 mb-4">The Thelwall Family Tree</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Tracing our roots and celebrating the generations that came before us
            </p>
            {selectedBranch && (
              <button
                onClick={() => setSelectedBranch(null)}
                className="mt-4 inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-full font-medium hover:bg-emerald-700 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                Show All Branches
              </button>
            )}
          </div>

          {/* Horizontal Timeline Layout */}
          <div className="relative min-w-max pb-12">
            {/* Timeline Line */}
            <div className="absolute top-32 left-0 right-0 h-1 bg-gradient-to-r from-emerald-300 via-teal-400 to-cyan-300"></div>

            <div className="flex gap-24 items-start relative">
              {/* Generation 1 - Grandparents */}
              <div className="flex flex-col items-center min-w-[280px]">
                <div className="bg-white rounded-2xl shadow-xl p-6 mb-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                  <div className="text-center mb-4">
                    <div className="inline-block px-4 py-2 bg-emerald-100 rounded-full mb-4">
                      <span className="text-xs font-bold text-emerald-700">GENERATION 1</span>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <div className="text-center group">
                      <div className="w-24 h-24 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-3xl font-bold shadow-lg mb-3 mx-auto group-hover:scale-110 transition-transform duration-300">
                        G
                      </div>
                      <h3 className="text-lg font-serif font-bold text-slate-900">George A Thelwall</h3>
                      <p className="text-sm text-slate-600">Grandfather</p>
                    </div>

                    <div className="h-px bg-slate-200"></div>

                    <div className="text-center group">
                      <div className="w-24 h-24 rounded-full bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center text-white text-3xl font-bold shadow-lg mb-3 mx-auto group-hover:scale-110 transition-transform duration-300">
                        M
                      </div>
                      <h3 className="text-lg font-serif font-bold text-slate-900">Thelwall</h3>
                      <p className="text-sm text-slate-600">Grandmother</p>
                    </div>
                  </div>
                </div>
                
                {/* Connection dot */}
                <div className="w-6 h-6 rounded-full bg-emerald-500 border-4 border-white shadow-lg relative z-10"></div>
              </div>

              {/* Generation 2 - Parents */}
              <div className="flex flex-col items-center min-w-[280px]">
                <div className="bg-white rounded-2xl shadow-xl p-6 mb-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                  <div className="text-center mb-4">
                    <div className="inline-block px-4 py-2 bg-teal-100 rounded-full mb-4">
                      <span className="text-xs font-bold text-teal-700">GENERATION 2</span>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <div className="text-center group">
                      <div className="w-24 h-24 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white text-3xl font-bold shadow-lg mb-3 mx-auto group-hover:scale-110 transition-transform duration-300">
                        E
                      </div>
                      <h3 className="text-lg font-serif font-bold text-slate-900">Eddie Thelwall</h3>
                      <p className="text-sm text-slate-600">Father</p>
                    </div>

                    <div className="h-px bg-slate-200"></div>

                    <div className="text-center group">
                      <div className="w-24 h-24 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center text-white text-3xl font-bold shadow-lg mb-3 mx-auto group-hover:scale-110 transition-transform duration-300">
                        L
                      </div>
                      <h3 className="text-lg font-serif font-bold text-slate-900">Laine Thelwall</h3>
                      <p className="text-sm text-slate-600">Mother</p>
                    </div>
                  </div>
                </div>
                
                {/* Connection dot */}
                <div className="w-6 h-6 rounded-full bg-teal-500 border-4 border-white shadow-lg relative z-10"></div>
              </div>

              {/* Generation 3 - Children (Eddy, Leslie, Darren, Lee) */}
              <div className="flex flex-col items-center min-w-[320px]">
                <div className="bg-white rounded-2xl shadow-xl p-6 mb-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                  <div className="text-center mb-4">
                    <div className="inline-block px-4 py-2 bg-cyan-100 rounded-full mb-4">
                      <span className="text-xs font-bold text-cyan-700">GENERATION 3</span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-6">
                    <div 
                      className={`text-center group cursor-pointer transition-all ${selectedBranch && selectedBranch !== 'eddy' ? 'opacity-30' : ''}`}
                      onClick={() => setSelectedBranch(selectedBranch === 'eddy' ? null : 'eddy')}
                    >
                      <div className={`w-20 h-20 rounded-full bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center text-white text-xl font-bold shadow-lg mb-2 mx-auto group-hover:scale-110 transition-transform duration-300 ${selectedBranch === 'eddy' ? 'ring-4 ring-blue-400' : ''}`}>
                        E
                      </div>
                      <h3 className="text-sm font-serif font-bold text-slate-900">Eddy A S Thelwall</h3>
                      <p className="text-xs text-slate-600">Son</p>
                    </div>

                    <div 
                      className={`text-center group cursor-pointer transition-all ${selectedBranch && selectedBranch !== 'leslie' ? 'opacity-30' : ''}`}
                      onClick={() => setSelectedBranch(selectedBranch === 'leslie' ? null : 'leslie')}
                    >
                      <div className={`w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white text-xl font-bold shadow-lg mb-2 mx-auto group-hover:scale-110 transition-transform duration-300 ${selectedBranch === 'leslie' ? 'ring-4 ring-green-400' : ''}`}>
                        L
                      </div>
                      <h3 className="text-sm font-serif font-bold text-slate-900">Leslie P Thelwall</h3>
                      <p className="text-xs text-slate-600">Son</p>
                    </div>

                    <div 
                      className={`text-center group cursor-pointer transition-all ${selectedBranch && selectedBranch !== 'darren' ? 'opacity-30' : ''}`}
                      onClick={() => setSelectedBranch(selectedBranch === 'darren' ? null : 'darren')}
                    >
                      <div className={`w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white text-xl font-bold shadow-lg mb-2 mx-auto group-hover:scale-110 transition-transform duration-300 ${selectedBranch === 'darren' ? 'ring-4 ring-purple-400' : ''}`}>
                        D
                      </div>
                      <h3 className="text-sm font-serif font-bold text-slate-900">Darren G F Thelwall</h3>
                      <p className="text-xs text-slate-600">Son</p>
                    </div>

                    <div 
                      className={`text-center group cursor-pointer transition-all ${selectedBranch && selectedBranch !== 'lee' ? 'opacity-30' : ''}`}
                      onClick={() => setSelectedBranch(selectedBranch === 'lee' ? null : 'lee')}
                    >
                      <div className={`w-20 h-20 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center text-white text-xl font-bold shadow-lg mb-2 mx-auto group-hover:scale-110 transition-transform duration-300 ${selectedBranch === 'lee' ? 'ring-4 ring-yellow-400' : ''}`}>
                        L
                      </div>
                      <h3 className="text-sm font-serif font-bold text-slate-900">Lee A Thelwall</h3>
                      <p className="text-xs text-slate-600">Son</p>
                    </div>
                  </div>
                </div>
                
                {/* Connection dot */}
                <div className="w-6 h-6 rounded-full bg-cyan-500 border-4 border-white shadow-lg relative z-10"></div>
              </div>

              {/* Generation 4 - Grandchildren */}
              <div className="flex flex-col items-center min-w-[900px]">
                <div className="bg-white rounded-2xl shadow-xl p-6 mb-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                  <div className="text-center mb-4">
                    <div className="inline-block px-4 py-2 bg-sky-100 rounded-full mb-4">
                      <span className="text-xs font-bold text-sky-700">GENERATION 4</span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-8">
                    {/* Eddy's Children */}
                    {(!selectedBranch || selectedBranch === 'eddy') && (
                      <div className="space-y-4">
                        <div className="text-center text-xs font-bold text-slate-500 mb-3 pb-2 border-b-2 border-blue-200">EDDY'S FAMILY</div>
                        
                        <div className="text-center group">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center text-white text-lg font-bold shadow-lg mb-2 mx-auto group-hover:scale-110 transition-transform duration-300">
                            A
                          </div>
                          <h3 className="text-xs font-serif font-bold text-slate-900">Aaron</h3>
                          <p className="text-xs text-slate-600">Son</p>
                        </div>

                        <div className="text-center group">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-white text-lg font-bold shadow-lg mb-2 mx-auto group-hover:scale-110 transition-transform duration-300">
                            T
                          </div>
                          <h3 className="text-xs font-serif font-bold text-slate-900">Tyler</h3>
                          <p className="text-xs text-slate-600">Son</p>
                        </div>

                        <div className="text-center group">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center text-white text-lg font-bold shadow-lg mb-2 mx-auto group-hover:scale-110 transition-transform duration-300">
                            R
                          </div>
                          <h3 className="text-xs font-serif font-bold text-slate-900">Remi</h3>
                          <p className="text-xs text-slate-600">Daughter</p>
                        </div>

                        <div className="text-center group">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-400 to-violet-500 flex items-center justify-center text-white text-lg font-bold shadow-lg mb-2 mx-auto group-hover:scale-110 transition-transform duration-300">
                            B
                          </div>
                          <h3 className="text-xs font-serif font-bold text-slate-900">Brooklyn</h3>
                          <p className="text-xs text-slate-600">Son</p>
                        </div>
                      </div>
                    )}

                    {/* Leslie's Children */}
                    {(!selectedBranch || selectedBranch === 'leslie') && (
                      <div className="space-y-4">
                        <div className="text-center text-xs font-bold text-slate-500 mb-3 pb-2 border-b-2 border-green-200">LESLIE'S FAMILY</div>
                        
                        <div className="text-center group">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center text-white text-lg font-bold shadow-lg mb-2 mx-auto group-hover:scale-110 transition-transform duration-300">
                            L
                          </div>
                          <h3 className="text-xs font-serif font-bold text-slate-900">Lewis</h3>
                          <p className="text-xs text-slate-600">Son</p>
                        </div>

                        <div className="text-center group">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-lime-400 to-green-500 flex items-center justify-center text-white text-lg font-bold shadow-lg mb-2 mx-auto group-hover:scale-110 transition-transform duration-300">
                            T
                          </div>
                          <h3 className="text-xs font-serif font-bold text-slate-900">Travis</h3>
                          <p className="text-xs text-slate-600">Son</p>
                        </div>

                        <div className="text-center group">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-fuchsia-400 to-pink-500 flex items-center justify-center text-white text-lg font-bold shadow-lg mb-2 mx-auto group-hover:scale-110 transition-transform duration-300">
                            T
                          </div>
                          <h3 className="text-xs font-serif font-bold text-slate-900">Tia</h3>
                          <p className="text-xs text-slate-600">Daughter</p>
                        </div>

                        <div className="text-center group">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center text-white text-lg font-bold shadow-lg mb-2 mx-auto group-hover:scale-110 transition-transform duration-300">
                            L
                          </div>
                          <h3 className="text-xs font-serif font-bold text-slate-900">Lloyd</h3>
                          <p className="text-xs text-slate-600">Son</p>
                        </div>
                      </div>
                    )}

                    {/* Darren's Family */}
                    {(!selectedBranch || selectedBranch === 'darren') && (
                      <div className="space-y-4">
                        <div className="text-center text-xs font-bold text-slate-500 mb-3 pb-2 border-b-2 border-purple-200">DARREN'S FAMILY</div>
                        
                        <div className="text-center group">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center text-white text-lg font-bold shadow-lg mb-2 mx-auto group-hover:scale-110 transition-transform duration-300">
                            C
                          </div>
                          <h3 className="text-xs font-serif font-bold text-slate-900">Claire M</h3>
                          <p className="text-xs text-slate-600">Wife</p>
                        </div>

                        <div className="text-center group">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white text-lg font-bold shadow-lg mb-2 mx-auto group-hover:scale-110 transition-transform duration-300">
                            J
                          </div>
                          <h3 className="text-xs font-serif font-bold text-slate-900">Joshua J</h3>
                          <p className="text-xs text-slate-600">Son</p>
                        </div>

                        <div className="text-center group">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-slate-400 to-gray-500 flex items-center justify-center text-white text-lg font-bold shadow-lg mb-2 mx-auto group-hover:scale-110 transition-transform duration-300">
                            B
                          </div>
                          <h3 className="text-xs font-serif font-bold text-slate-900">Benjamin W</h3>
                          <p className="text-xs text-slate-600">Step-son</p>
                        </div>

                        <div className="text-center group">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center text-white text-lg font-bold shadow-lg mb-2 mx-auto group-hover:scale-110 transition-transform duration-300">
                            G
                          </div>
                          <h3 className="text-xs font-serif font-bold text-slate-900">Grace M</h3>
                          <p className="text-xs text-slate-600">Step-daughter</p>
                        </div>
                      </div>
                    )}

                    {/* Lee's Children */}
                    {(!selectedBranch || selectedBranch === 'lee') && (
                      <div className="space-y-4">
                        <div className="text-center text-xs font-bold text-slate-500 mb-3 pb-2 border-b-2 border-amber-200">LEE'S FAMILY</div>
                        
                        <div className="text-center group">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-400 to-teal-500 flex items-center justify-center text-white text-lg font-bold shadow-lg mb-2 mx-auto group-hover:scale-110 transition-transform duration-300">
                            B
                          </div>
                          <h3 className="text-xs font-serif font-bold text-slate-900">Benjamin</h3>
                          <p className="text-xs text-slate-600">Son</p>
                        </div>

                        <div className="text-center group">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-400 to-orange-500 flex items-center justify-center text-white text-lg font-bold shadow-lg mb-2 mx-auto group-hover:scale-110 transition-transform duration-300">
                            J
                          </div>
                          <h3 className="text-xs font-serif font-bold text-slate-900">Jake</h3>
                          <p className="text-xs text-slate-600">Son</p>
                        </div>

                        <div className="text-center group">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-lime-600 flex items-center justify-center text-white text-lg font-bold shadow-lg mb-2 mx-auto group-hover:scale-110 transition-transform duration-300">
                            T
                          </div>
                          <h3 className="text-xs font-serif font-bold text-slate-900">Tommy</h3>
                          <p className="text-xs text-slate-600">Son</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Connection dot */}
                <div className="w-6 h-6 rounded-full bg-sky-500 border-4 border-white shadow-lg relative z-10"></div>
              </div>

              {/* Generation 5 - Great-Grandchildren */}
              <div className="flex flex-col items-center min-w-[280px]">
                <div className="bg-white rounded-2xl shadow-xl p-6 mb-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                  <div className="text-center mb-4">
                    <div className="inline-block px-4 py-2 bg-indigo-100 rounded-full mb-4">
                      <span className="text-xs font-bold text-indigo-700">GENERATION 5</span>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    {/* Aaron's Family */}
                    {(!selectedBranch || selectedBranch === 'eddy') && (
                      <>
                        <div className="text-center text-xs font-bold text-slate-500 mb-3 pb-2 border-b-2 border-teal-200">AARON'S FAMILY</div>
                        
                        <div className="text-center group">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center text-white text-lg font-bold shadow-lg mb-2 mx-auto group-hover:scale-110 transition-transform duration-300">
                            L
                          </div>
                          <h3 className="text-xs font-serif font-bold text-slate-900">Lauren</h3>
                          <p className="text-xs text-slate-600">Wife</p>
                        </div>
                      </>
                    )}

                    {/* Grace's Family */}
                    {(!selectedBranch || selectedBranch === 'darren') && (
                      <>
                        <div className="text-center text-xs font-bold text-slate-500 mb-3 pb-2 border-b-2 border-rose-200 mt-6">GRACE'S FAMILY</div>
                        
                        <div className="text-center group">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-lg font-bold shadow-lg mb-2 mx-auto group-hover:scale-110 transition-transform duration-300">
                            J
                          </div>
                          <h3 className="text-xs font-serif font-bold text-slate-900">Jake Chequer</h3>
                          <p className="text-xs text-slate-600">Fiancé</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Connection dot */}
                <div className="w-6 h-6 rounded-full bg-indigo-500 border-4 border-white shadow-lg relative z-10"></div>
              </div>
            </div>

            {/* Scroll hint */}
            <div className="text-center mt-8">
              <p className="text-sm text-slate-500 flex items-center justify-center gap-2">
                <i className="ri-arrow-left-right-line"></i>
                {selectedBranch ? 'Click "Show All Branches" to see the full family tree' : 'Scroll horizontally to explore · Click on any Generation 3 member to filter their branch'}
              </p>
            </div>
          </div>

          {/* Family Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-20">
            <div className="bg-white rounded-2xl p-6 shadow-lg text-center hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-amber-400 to-orange-500 rounded-full mx-auto mb-4">
                <i className="ri-team-line text-2xl text-white"></i>
              </div>
              <h4 className="text-3xl font-bold text-slate-900 mb-2">5</h4>
              <p className="text-slate-600 font-medium">Generations</p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg text-center hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-rose-400 to-pink-500 rounded-full mx-auto mb-4">
                <i className="ri-heart-line text-2xl text-white"></i>
              </div>
              <h4 className="text-3xl font-bold text-slate-900 mb-2">25</h4>
              <p className="text-slate-600 font-medium">Family Members</p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg text-center hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full mx-auto mb-4">
                <i className="ri-time-line text-2xl text-white"></i>
              </div>
              <h4 className="text-3xl font-bold text-slate-900 mb-2">80+</h4>
              <p className="text-slate-600 font-medium">Years of History</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pets Section - NEW */}
      <section id="pets" className="py-24 bg-gradient-to-br from-teal-50 via-cyan-50 to-sky-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-teal-100 rounded-full mb-4">
              <span className="text-xs font-semibold text-teal-700 tracking-wider uppercase">Furry Family Members</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-serif font-bold text-slate-900 mb-4">Our Beloved Pets</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Meet the four-legged members of our family who bring us endless joy and unconditional love
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
            {/* Pet Card - Nala */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group">
              <div 
                className="relative w-full h-80 cursor-pointer"
                onClick={() => scrollToSection('nala-gallery')}
              >
                <img 
                  src="https://static.readdy.ai/image/00835a872c246035d3f2fdf431da5d67/2b25bb160901aac367f423a9ad571388.jpeg" 
                  alt="Nala the Cockapoo" 
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute top-4 right-4 w-12 h-12 flex items-center justify-center bg-amber-500 rounded-full shadow-lg">
                  <i className="ri-heart-fill text-white text-xl"></i>
                </div>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 px-6 py-3 rounded-full">
                    <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                      View Photo Gallery
                      <i className="ri-arrow-down-line"></i>
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-8 space-y-4">
                <h3 className="text-3xl font-serif font-bold text-slate-900">Nala</h3>
                <div className="flex items-center gap-2 text-slate-600">
                  <i className="ri-paw-print-line text-amber-600 text-xl"></i>
                  <span className="text-base font-medium">Cockapoo</span>
                </div>
                <p className="text-slate-600 leading-relaxed">
                  Our adorable companion who loves cuddles and playtime. Nala has been part of our family for 5 years and brings joy to everyone she meets.
                </p>
                <div className="flex items-center gap-6 pt-3 text-sm text-slate-500">
                  <div className="flex items-center gap-2">
                    <i className="ri-cake-line text-lg"></i>
                    <span>5 years old</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-home-heart-line text-lg"></i>
                    <span>Since 2020</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Pet Card - Bentley */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group">
              <div 
                className="relative w-full h-80 cursor-pointer"
                onClick={() => scrollToSection('bentley-gallery')}
              >
                <img 
                  src="https://static.readdy.ai/image/00835a872c246035d3f2fdf431da5d67/34ee4db7b23953a4a743e35327d62b4e.jpeg" 
                  alt="Bentley the Cocker Spaniel" 
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute top-4 right-4 w-12 h-12 flex items-center justify-center bg-orange-500 rounded-full shadow-lg">
                  <i className="ri-heart-fill text-white text-xl"></i>
                </div>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 px-6 py-3 rounded-full">
                    <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                      View Photo Gallery
                      <i className="ri-arrow-down-line"></i>
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-8 space-y-4">
                <h3 className="text-3xl font-serif font-bold text-slate-900">Bentley</h3>
                <div className="flex items-center gap-2 text-slate-600">
                  <i className="ri-paw-print-line text-orange-600 text-xl"></i>
                  <span className="text-base font-medium">Cocker Spaniel</span>
                </div>
                <p className="text-slate-600 leading-relaxed">
                  Our energetic and loyal friend who loves long walks and belly rubs. Bentley has been bringing happiness to our family with his playful spirit and gentle nature.
                </p>
                <div className="flex items-center gap-6 pt-3 text-sm text-slate-500">
                  <div className="flex items-center gap-2">
                    <i className="ri-cake-line text-lg"></i>
                    <span>4 years old</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-home-heart-line text-lg"></i>
                    <span>Since 2021</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Nala's Photo Gallery */}
          <div className="mb-16" id="nala-gallery">
            <div className="text-center mb-10">
              <h3 className="text-3xl font-serif font-bold text-slate-900 mb-3">Nala's Photo Gallery</h3>
              <p className="text-slate-600">Capturing Nala's cutest moments - 24 precious memories</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {/* Photo 1 */}
              <div className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer h-72">
                <img 
                  src="https://static.readdy.ai/image/00835a872c246035d3f2fdf431da5d67/2b25bb160901aac367f423a9ad571388.jpeg" 
                  alt="Nala photo 1" 
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 text-white">
                    <p className="text-sm font-semibold">Photo 1</p>
                  </div>
                </div>
              </div>

              {/* Photo 2 */}
              <div className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer h-72">
                <img 
                  src="https://static.readdy.ai/image/00835a872c246035d3f2fdf431da5d67/c5ad60a3e72983c301e6bfd17a8fea0d.jpeg" 
                  alt="Nala photo 2" 
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 text-white">
                    <p className="text-sm font-semibold">Photo 2</p>
                  </div>
                </div>
              </div>

              {/* Photo 3 */}
              <div className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer h-72">
                <img 
                  src="https://static.readdy.ai/image/00835a872c246035d3f2fdf431da5d67/78fb29ef1213215c66fbd3689a7f433e.jpeg" 
                  alt="Nala photo 3" 
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 text-white">
                    <p className="text-sm font-semibold">Photo 3</p>
                  </div>
                </div>
              </div>

              {/* Photo 4 */}
              <div className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer h-72">
                <img 
                  src="https://static.readdy.ai/image/00835a872c246035d3f2fdf431da5d67/05ef1b8431a69be87328f94b22408dd9.jpeg" 
                  alt="Nala photo 4" 
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 text-white">
                    <p className="text-sm font-semibold">Photo 4</p>
                  </div>
                </div>
              </div>

              {/* Photo 5 */}
              <div className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer h-72">
                <img 
                  src="https://static.readdy.ai/image/00835a872c246035d3f2fdf431da5d67/9773eab02a59859250d83573de4cb76a.jpeg" 
                  alt="Nala photo 5" 
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 text-white">
                    <p className="text-sm font-semibold">Photo 5</p>
                  </div>
                </div>
              </div>

              {/* Photo 6 */}
              <div className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer h-72">
                <img 
                  src="https://static.readdy.ai/image/00835a872c246035d3f2fdf431da5d67/84aefb38f4e345e3aafc8d2e29a989fd.jpeg" 
                  alt="Nala photo 6" 
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 text-white">
                    <p className="text-sm font-semibold">Photo 6</p>
                  </div>
                </div>
              </div>

              {/* Photos 7-24 - Placeholders with Upload */}
              {[7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24].map((num) => (
                <div 
                  key={num} 
                  className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer h-72"
                  onClick={() => openUploadModal(num)}
                >
                  {uploadedPhotos[num] ? (
                    <>
                      <img 
                        src={uploadedPhotos[num]} 
                        alt={`Nala photo ${num}`} 
                        className="w-full h-full object-cover object-top"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="absolute bottom-4 left-4 text-white">
                          <p className="text-sm font-semibold">Photo {num}</p>
                        </div>
                        <div className="absolute top-4 right-4">
                          <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full backdrop-blur-sm">
                            <i className="ri-edit-line text-white text-lg"></i>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="bg-gradient-to-br from-teal-100 to-cyan-100 flex items-center justify-center h-full group-hover:from-teal-200 group-hover:to-cyan-200 transition-all">
                      <div className="text-center p-6">
                        <div className="w-16 h-16 flex items-center justify-center bg-white/50 rounded-full mx-auto mb-3 group-hover:bg-white/70 group-hover:scale-110 transition-all">
                          <i className="ri-image-add-line text-3xl text-teal-600"></i>
                        </div>
                        <p className="text-base font-semibold text-slate-800 mb-1">Photo {num}</p>
                        <p className="text-xs text-slate-600">Click to upload</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Bentley's Photo Gallery */}
          <div className="mb-16" id="bentley-gallery">
            <div className="text-center mb-10">
              <h3 className="text-3xl font-serif font-bold text-slate-900 mb-3">Bentley's Photo Gallery</h3>
              <p className="text-slate-600">Capturing Bentley's best moments - 24 precious memories</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {/* Photos 1-24 - Placeholders with Upload */}
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24].map((num) => (
                <div 
                  key={num} 
                  className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer h-72"
                  onClick={() => openBentleyUploadModal(num)}
                >
                  {uploadedBentleyPhotos[num] ? (
                    <>
                      <img 
                        src={uploadedBentleyPhotos[num]} 
                        alt={`Bentley photo ${num}`} 
                        className="w-full h-full object-cover object-top"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="absolute bottom-4 left-4 text-white">
                          <p className="text-sm font-semibold">Photo {num}</p>
                        </div>
                        <div className="absolute top-4 right-4">
                          <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full backdrop-blur-sm">
                            <i className="ri-edit-line text-white text-lg"></i>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center h-full group-hover:from-orange-200 group-hover:to-amber-200 transition-all">
                      <div className="text-center p-6">
                        <div className="w-16 h-16 flex items-center justify-center bg-white/50 rounded-full mx-auto mb-3 group-hover:bg-white/70 group-hover:scale-110 transition-all">
                          <i className="ri-image-add-line text-3xl text-orange-600"></i>
                        </div>
                        <p className="text-base font-semibold text-slate-800 mb-1">Photo {num}</p>
                        <p className="text-xs text-slate-600">Click to upload</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Pet Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-2xl p-6 shadow-lg text-center hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-amber-400 to-orange-500 rounded-full mx-auto mb-4">
                <i className="ri-paw-print-fill text-2xl text-white"></i>
              </div>
              <h4 className="text-3xl font-bold text-slate-900 mb-2">2</h4>
              <p className="text-slate-600 font-medium">Beloved Pets</p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg text-center hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-rose-400 to-pink-500 rounded-full mx-auto mb-4">
                <i className="ri-heart-pulse-line text-2xl text-white"></i>
              </div>
              <h4 className="text-3xl font-bold text-slate-900 mb-2">∞</h4>
              <p className="text-slate-600 font-medium">Love & Joy</p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg text-center hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full mx-auto mb-4">
                <i className="ri-walk-line text-2xl text-white"></i>
              </div>
              <h4 className="text-3xl font-bold text-slate-900 mb-2">Daily</h4>
              <p className="text-slate-600 font-medium">Adventures</p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg text-center hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-cyan-400 to-sky-500 rounded-full mx-auto mb-4">
                <i className="ri-camera-line text-2xl text-white"></i>
              </div>
              <h4 className="text-3xl font-bold text-slate-900 mb-2">1000+</h4>
              <p className="text-slate-600 font-medium">Pet Photos</p>
            </div>
          </div>
        </div>
      </section>

      {/* Family Stories Section */}
      <section id="stories" className="py-24 bg-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="ri-double-quotes-l text-5xl text-amber-600"></i>
            </div>
            <h2 className="text-4xl lg:text-5xl font-serif font-bold text-slate-900">Our Family Stories</h2>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="bg-gradient-to-br from-rose-100 to-orange-100 rounded-3xl p-8 space-y-4 hover:scale-105 transition-transform duration-300 cursor-pointer">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <i className="ri-calendar-line"></i>
                <span>Coming Soon</span>
              </div>
              <h3 className="text-2xl font-serif font-bold text-slate-900">Holding Page - Will Update Soon</h3>
              <p className="text-slate-700 leading-relaxed">
                We're gathering our favorite family stories to share with you. Check back soon for heartwarming tales and memorable moments from our family's journey together.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gradient-to-br from-amber-600 to-orange-600">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            <div className="lg:col-span-2 space-y-8 text-white">
              <div>
                <h2 className="text-4xl lg:text-5xl font-serif font-bold mb-4">Let's Connect</h2>
                <p className="text-amber-100 text-lg">
                  We'd love to hear from you! Whether you want to share your own family stories or just say hello, feel free to reach out.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <div>
                    <p className="font-medium mb-1">Email</p>
                    <p className="text-amber-100">Darren.Thelwall@Thelwall.club</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
                    <i className="ri-map-pin-line text-xl"></i>
                  </div>
                  <div>
                    <p className="font-medium mb-1">Location</p>
                    <p className="text-amber-100">Swindon, England</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 pt-4">
                <a href="#" className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
                <a href="#" className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="#" className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-colors cursor-pointer">
                  <i className="ri-pinterest-fill text-xl"></i>
                </a>
              </div>
            </div>

            <div className="lg:col-span-3">
              <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 shadow-2xl space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">Your Name</label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm"
                    placeholder="John Doe"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm"
                    placeholder="john@example.com"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">Message</label>
                  <textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={5}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all resize-none text-sm"
                    placeholder="Share your thoughts with us..."
                    required
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full px-8 py-4 bg-amber-600 text-white rounded-full font-medium hover:bg-amber-700 transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  Send Message
                  <i className="ri-send-plane-fill"></i>
                </button>

                {showSuccess && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm">
                    Thank you for your message! We'll get back to you soon.
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <img 
                  src="https://static.readdy.ai/image/00835a872c246035d3f2fdf431da5d67/4539d659f03c970f77d9da74a1ca923d.jpeg" 
                  alt="Family Logo" 
                  className="w-10 h-10 rounded-full object-cover"
                />
                <span className="text-xl font-serif font-bold text-white">Family Memories</span>
              </div>
              <p className="text-slate-400 leading-relaxed">
                Capturing life's precious moments, one photo at a time. Creating memories that last forever.
              </p>
              <p className="text-sm text-slate-500">© 2025 Anderson Family. All rights reserved.</p>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">Quick Links</h3>
              <div className="space-y-3">
                <button onClick={() => scrollToSection('home')} className="block text-slate-400 hover:text-amber-400 transition-colors cursor-pointer whitespace-nowrap">Home</button>
                <button onClick={() => scrollToSection('gallery')} className="block text-slate-400 hover:text-amber-400 transition-colors cursor-pointer whitespace-nowrap">Gallery</button>
                <button onClick={() => scrollToSection('about')} className="block text-slate-400 hover:text-amber-400 transition-colors cursor-pointer whitespace-nowrap">About Us</button>
                <button onClick={() => scrollToSection('family-tree')} className="block text-slate-400 hover:text-amber-400 transition-colors cursor-pointer whitespace-nowrap">Family Tree</button>
                <button onClick={() => scrollToSection('pets')} className="block text-slate-400 hover:text-amber-400 transition-colors cursor-pointer whitespace-nowrap">Our Pets</button>
                <button onClick={() => scrollToSection('stories')} className="block text-slate-400 hover:text-amber-400 transition-colors cursor-pointer whitespace-nowrap">Family Stories</button>
                <button onClick={() => scrollToSection('contact')} className="block text-slate-400 hover:text-amber-400 transition-colors cursor-pointer whitespace-nowrap">Contact</button>
              </div>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">Stay Updated</h3>
              <p className="text-slate-400 mb-4 text-sm">Subscribe to receive updates about our latest family adventures.</p>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="Your email"
                  className="flex-1 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm text-white placeholder-slate-500"
                />
                <button className="px-6 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition-colors cursor-pointer whitespace-nowrap">
                  Subscribe
                </button>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8 text-center">
            <p className="text-slate-500 text-sm">
              Made with <span className="text-red-500">❤️</span> for the Anderson Family · <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 transition-colors">Website Builder</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
